import { RefreshCw } from "lucide-react";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

/**
 * PageWrapper — standard page shell for all non-dashboard pages.
 *
 * Props:
 *   fabSpacing   — page has a floating FAB above the nav (uses 160px bottom clearance)
 *   hasBottomNav — false for pages without bottom nav (uses pb-8 only)
 *   onRefresh    — async function; when provided enables pull-to-refresh
 *   className    — passthrough for page-specific overrides
 *   testId       — data-testid on the root div
 */
export default function PageWrapper({
  children,
  className = "",
  fabSpacing = false,
  hasBottomNav = true,
  testId,
  onRefresh,
}) {
  const pb = !hasBottomNav
    ? "pb-8"
    : fabSpacing
    ? "pb-[calc(176px+env(safe-area-inset-bottom))]"
    : "pb-[calc(96px+env(safe-area-inset-bottom))]";

  const { containerRef, pullProgress, refreshing } = usePullToRefresh(
    onRefresh,
    { enabled: !!onRefresh }
  );

  return (
    <div
      ref={containerRef}
      className={`anim-in min-h-[100dvh] bg-[var(--kv-page-bg)] ${pb} ${className}`}
      data-testid={testId}
    >
      {onRefresh && (pullProgress > 0 || refreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden"
          style={{
            height: `${Math.min(pullProgress, 1) * 44}px`,
            opacity: Math.min(pullProgress * 1.4, 1),
            transition: refreshing ? "none" : "height 150ms ease-out, opacity 150ms ease-out",
          }}
        >
          <RefreshCw
            size={16}
            className={`text-slate-400 dark:text-slate-500 ${refreshing ? "animate-spin" : ""}`}
            style={{
              transform: `rotate(${Math.min(pullProgress, 1) * 220}deg) scale(${0.7 + Math.min(pullProgress, 1) * 0.3})`,
              transition: refreshing ? "none" : "transform 100ms ease-out",
            }}
          />
        </div>
      )}
      {children}
    </div>
  );
}
